import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileVideo, Info, Library, ListFilter, Plus, Search, Tag, X } from 'lucide-react'
import { programCategoriesApi, programsApi, seriesApi } from '../../lib/api.js'
import type { CgOverlayProgramScope, CgOverlayProgramTarget, CgOverlayTargetKind } from '../../types/index.js'
import type { ProgramCategory } from '../../types/domain.js'
import { Spinner } from '../common/Spinner.js'
import {
  SCOPE_LABELS,
  TARGET_KIND_LABELS,
  cgOverlayScopeSummary,
  matchesCatalogSearch,
  programTargetLabel,
} from './cgOverlayProgramScope.js'

const SEARCH_DELAY_MS = 250
const RESULTS_PER_KIND = 6

const KIND_ICONS: Record<CgOverlayTargetKind, typeof Library> = {
  SERIES: Library,
  PROGRAM: FileVideo,
  CATEGORY: Tag,
}

const KIND_GROUP_TITLES: Record<CgOverlayTargetKind, string> = {
  SERIES: 'Émissions — toutes leurs éditions',
  PROGRAM: 'Fiches isolées',
  CATEGORY: 'Catégories éditoriales',
}

const KIND_ORDER: CgOverlayTargetKind[] = ['SERIES', 'PROGRAM', 'CATEGORY']

type Candidate = Omit<CgOverlayProgramTarget, 'missing'>

interface Props {
  active: boolean
  channelId: string
  scope: CgOverlayProgramScope
  targets: CgOverlayProgramTarget[]
  onScopeChange: (scope: CgOverlayProgramScope) => void
  onTargetsChange: (targets: CgOverlayProgramTarget[]) => void
}

export const CgOverlayProgramScopeSection = memo(function CgOverlayProgramScopeSection({
  active,
  channelId,
  scope,
  targets,
  onScopeChange,
  onTargetsChange,
}: Props) {
  const [search, setSearch] = useState('')
  const [categories, setCategories] = useState<ProgramCategory[] | null>(null)
  const [remote, setRemote] = useState<Candidate[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const request = useRef(0)
  const targeted = scope !== 'ALL'

  // Les categories tiennent en une liste courte : chargees une fois, filtrees
  // sur le poste.
  useEffect(() => {
    if (!active || !targeted || categories) return
    programCategoriesApi.list({ channelId })
      .then(rows => setCategories(rows.filter(row => row.active !== false)))
      .catch(() => setError('Catégories indisponibles.'))
  }, [active, categories, channelId, targeted])

  useEffect(() => { setCategories(null) }, [channelId])

  useEffect(() => {
    const query = search.trim()
    const current = ++request.current
    if (!active || !targeted || query.length < 2) {
      setRemote([])
      setSearching(false)
      return
    }
    setSearching(true)
    const timer = window.setTimeout(() => {
      Promise.all([
        seriesApi.list({ channelId, search: query }),
        programsApi.list({ channelId, catalog: 'programs', search: query, limit: RESULTS_PER_KIND, sortBy: 'premiereDate', sortDir: 'desc' }),
      ]).then(([series, programs]) => {
        if (current !== request.current) return
        const programRows: Array<{ id: string; title: string; plannedFirstAirDate?: string | null }> = programs?.data ?? []
        setRemote([
          ...series.slice(0, RESULTS_PER_KIND).map(row => ({ kind: 'SERIES' as const, id: row.id, label: row.title })),
          ...programRows.map(row => ({ kind: 'PROGRAM' as const, id: row.id, label: programTargetLabel(row) })),
        ])
        setError('')
      }).catch(() => {
        if (current === request.current) setError('Recherche impossible pour le moment.')
      }).finally(() => {
        if (current === request.current) setSearching(false)
      })
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [active, channelId, search, targeted])

  const selectedIds = useMemo(() => new Set(targets.map(target => `${target.kind}:${target.id}`)), [targets])

  const groups = useMemo(() => {
    const query = search.trim()
    const categoryCandidates: Candidate[] = query
      ? (categories ?? [])
        .filter(category => matchesCatalogSearch(category.label, query))
        .slice(0, RESULTS_PER_KIND)
        .map(category => ({ kind: 'CATEGORY', id: category.id, label: category.label }))
      : []
    const all = [...remote, ...categoryCandidates].filter(candidate => !selectedIds.has(`${candidate.kind}:${candidate.id}`))
    return KIND_ORDER
      .map(kind => ({ kind, items: all.filter(candidate => candidate.kind === kind) }))
      .filter(group => group.items.length > 0)
  }, [categories, remote, search, selectedIds])

  const addTarget = useCallback((candidate: Candidate) => {
    onTargetsChange([...targets, { ...candidate, missing: false }])
  }, [onTargetsChange, targets])

  const removeTarget = useCallback((target: CgOverlayProgramTarget) => {
    onTargetsChange(targets.filter(item => item.kind !== target.kind || item.id !== target.id))
  }, [onTargetsChange, targets])

  const query = search.trim()
  const emptyTargets = targeted && targets.length === 0

  return (
    <section className="cg-form-section" aria-labelledby="cg-program-scope-title">
      <h3 id="cg-program-scope-title"><ListFilter size={14} aria-hidden="true" />Programmes concernés</h3>
      <div className="cg-segmented cg-segmented--labels" role="radiogroup" aria-label="Programmes concernés">
        {(Object.keys(SCOPE_LABELS) as CgOverlayProgramScope[]).map(value => (
          <button
            type="button"
            key={value}
            role="radio"
            aria-checked={scope === value}
            className={scope === value ? 'is-active' : ''}
            onClick={() => onScopeChange(value)}
          >
            <span>{SCOPE_LABELS[value]}</span>
          </button>
        ))}
      </div>

      {targeted && (
        <>
          {targets.length > 0 && (
            <ul className="cg-scope-targets" aria-label="Cibles retenues">
              {targets.map(target => {
                const Icon = KIND_ICONS[target.kind]
                return (
                  <li key={`${target.kind}:${target.id}`} className={`cg-scope-target${target.missing ? ' is-missing' : ''}`}>
                    <Icon size={12} aria-hidden="true" />
                    <span className="cg-scope-target__label">{target.label}</span>
                    <small>{target.missing ? 'supprimé du catalogue' : TARGET_KIND_LABELS[target.kind]}</small>
                    <button
                      type="button"
                      className="cg-scope-target__remove"
                      onClick={() => removeTarget(target)}
                      aria-label={`Retirer ${target.label} (${TARGET_KIND_LABELS[target.kind]})`}
                      title="Retirer"
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <label className="cg-field">
            <span>{scope === 'INCLUDE' ? 'Ajouter une émission, une fiche ou une catégorie' : 'Écarter une émission, une fiche ou une catégorie'}</span>
            <span className="cg-scope-search">
              <Search size={13} aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Rechercher au catalogue (2 lettres minimum)"
                autoComplete="off"
              />
              {searching && <Spinner />}
            </span>
          </label>

          {query.length >= 2 && (groups.length > 0 || !searching) && (
            <div className="cg-scope-results" aria-live="polite">
              {groups.map(group => {
                const Icon = KIND_ICONS[group.kind]
                return (
                  <div className="cg-scope-results__group" key={group.kind}>
                    <strong>{KIND_GROUP_TITLES[group.kind]}</strong>
                    {group.items.map(candidate => (
                      <button
                        type="button"
                        key={`${candidate.kind}:${candidate.id}`}
                        className="cg-scope-result"
                        onClick={() => addTarget(candidate)}
                        aria-label={`Ajouter ${candidate.label} (${TARGET_KIND_LABELS[candidate.kind]})`}
                      >
                        <Icon size={12} aria-hidden="true" />
                        <span>{candidate.label}</span>
                        <Plus size={12} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )
              })}
              {!searching && groups.length === 0 && <div className="cg-empty">Aucun résultat pour « {query} »</div>}
            </div>
          )}
          {error && <span className="cg-inline-message"><Info size={12} aria-hidden="true" />{error}</span>}
        </>
      )}

      <div className={`cg-timing-summary${emptyTargets ? ' is-warning' : ''}`} role={emptyTargets ? 'status' : undefined}>
        <Info size={13} aria-hidden="true" />{cgOverlayScopeSummary(scope, targets)}
      </div>
    </section>
  )
})
